import { useEffect, useMemo, useState } from "react";
import platformAPI from "../../../services/platformApi";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const STATUS = {
  pending: "На проверке",
  active: "Активен",
  paused: "Пауза",
  rejected: "Отклонён",
};

export default function PlatformPartners() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    platformAPI
      .getPartners()
      .then((payload) => {
        setData(payload);
        setError("");
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Не удалось загрузить партнёров");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const partners = useMemo(() => {
    const rows = data?.partners || [];
    if (filter === "all") return rows;
    return rows.filter((row) => row.status === filter);
  }, [data, filter]);

  const save = async (id, body) => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.updatePartner(id, body);
      setOpenId(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.error || "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  const stats = data?.stats || {};

  return (
    <div className={s.page}>
      <PlatformPageHeader
        mobileOnlyBack
        backTo="/admin/more"
        backLabel="Ещё"
        title="Партнёры"
        lead="Кто привёл клуб, получает месяц бесплатно после первой оплаты. Приведённому клубу — скидка 20% на 3 месяца."
      />
      {error ? <div className={s.alertError}>{error}</div> : null}
      <div className={s.grid}>
        <article className={s.card}>
          <div className={s.cardLabel}>На проверке</div>
          <div className={s.cardValue}>{stats.pending || 0}</div>
        </article>
        <article className={s.card}>
          <div className={s.cardLabel}>Активные</div>
          <div className={s.cardValue}>{stats.active || 0}</div>
        </article>
        <article className={s.card}>
          <div className={s.cardLabel}>Клубы</div>
          <div className={s.cardValue}>{stats.referrals || 0}</div>
        </article>
        <article className={s.card}>
          <div className={s.cardLabel}>Месяцев начислено</div>
          <div className={s.cardValue}>{stats.rewarded || 0}</div>
        </article>
      </div>

      <div className={s.panelHeaderRow}>
        {[
          ["pending", "Заявки"],
          ["active", "Активные"],
          ["paused", "Пауза"],
          ["rejected", "Отказы"],
          ["all", "Все"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={filter === id ? `${s.btn} ${s.btnPrimary}` : s.btn}
            onClick={() => setFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? <div className={s.loading}>Загрузка…</div> : null}

      {!loading && partners.length === 0 ? (
        <p className={s.emptyHint}>В этом списке никого нет.</p>
      ) : null}

      <div style={{ display: "grid", gap: 12 }}>
        {partners.map((row) => {
          const open = openId === row.id;
          const local = draft[row.id] || {
            admin_note: row.admin_note || "",
          };
          return (
            <article key={row.id} className={s.panel}>
              <div className={s.mobileDataCardTop}>
                <strong>{row.full_name}</strong>
                <span className={s.statusPill}>{STATUS[row.status] || row.status}</span>
              </div>
              <p className={s.panelHint}>
                {row.email}
                {row.company ? ` · ${row.company}` : ""} · код {row.code}
              </p>
              <p className={s.panelHint}>
                {row.referrals} клубов · {row.paying} оплатили · месяцев начислено {row.rewarded || 0}
              </p>
              {row.about ? <p className={s.panelHint}>{row.about}</p> : null}
              <button
                type="button"
                className={s.btn}
                onClick={() => setOpenId(open ? null : row.id)}
              >
                {open ? "Свернуть" : "Решение"}
              </button>
              {open ? (
                <div style={{ marginTop: 12 }}>
                  {(row.clubs || []).length ? (
                    row.clubs.map((club) => (
                      <p key={club.id} className={s.panelHint}>
                        {club.name}
                        {club.reward_granted_at ? " · месяц начислен" : " · ждём оплату"}
                      </p>
                    ))
                  ) : (
                    <p className={s.panelHint}>Клубов пока нет.</p>
                  )}
                  <label className={s.field}>
                    <span>Комментарий партнёру</span>
                    <textarea
                      value={local.admin_note}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          [row.id]: { ...local, admin_note: event.target.value },
                        }))
                      }
                    />
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button
                      type="button"
                      className={`${s.btn} ${s.btnPrimary}`}
                      disabled={busy}
                      onClick={() =>
                        save(row.id, {
                          status: "active",
                          admin_note: local.admin_note,
                        })
                      }
                    >
                      Включить
                    </button>
                    <button
                      type="button"
                      className={s.btn}
                      disabled={busy}
                      onClick={() =>
                        save(row.id, {
                          status: "paused",
                          admin_note: local.admin_note,
                        })
                      }
                    >
                      Пауза
                    </button>
                    <button
                      type="button"
                      className={`${s.btn} ${s.btnDanger}`}
                      disabled={busy}
                      onClick={() =>
                        save(row.id, {
                          status: "rejected",
                          admin_note: local.admin_note,
                        })
                      }
                    >
                      Отключить
                    </button>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

    </div>
  );
}
